// Pooled coin/soul/sparkle particles with arc flights and a hard cap (docs/07 §2 global rules).
import { AnimatedSprite, Container, type Texture } from 'pixi.js';
import { B } from '../../content/balance';
import { getAtlas } from '../../art/rasterize';
import { clock } from '../timeline/clock';

interface Point {
  x: number;
  y: number;
}

export class Particles {
  readonly layer = new Container();
  private pool: AnimatedSprite[] = [];
  private live = 0;

  private frames(id: string): Texture[] {
    const a = getAtlas();
    return Array.from({ length: a.frameCount(id) || 1 }, (_, i) => a.tex(id, i));
  }

  private take(id: string): AnimatedSprite | null {
    if (this.live >= B.PARTICLE_CAP) return null;
    const s = this.pool.pop() ?? new AnimatedSprite(this.frames(id));
    s.textures = this.frames(id);
    s.anchor.set(0.5);
    s.animationSpeed = 0.25;
    s.loop = true;
    s.alpha = 1;
    s.scale.set(1);
    s.visible = true;
    s.play();
    this.layer.addChild(s);
    this.live++;
    return s;
  }

  private give(s: AnimatedSprite): void {
    s.stop();
    s.visible = false;
    this.layer.removeChild(s);
    this.pool.push(s);
    this.live--;
  }

  /**
   * Fly `count` particles along arcs from → to. Beyond the cap, particles merge into bigger ones.
   * Resolves when the last particle lands; `onLand` fires per particle (for count-ups/ticks).
   */
  async flight(id: string, from: Point, to: Point, count: number, ms: number, onLand?: (i: number) => void): Promise<void> {
    if (clock.isSkipping || count <= 0) return;
    const room = Math.max(1, B.PARTICLE_CAP - this.live);
    const n = Math.min(count, room, 60);
    const merge = Math.min(3, Math.ceil(count / n));
    const jobs: Promise<void>[] = [];
    for (let i = 0; i < n; i++) {
      const s = this.take(id);
      if (!s) break;
      s.scale.set(merge);
      const sx = from.x + (Math.random() - 0.5) * 24;
      const sy = from.y + (Math.random() - 0.5) * 16;
      s.position.set(sx, sy);
      const lift = 30 + Math.random() * 40;
      const path = { t: 0 };
      const delay = (i / n) * ms * 0.5;
      jobs.push(
        clock.wait(delay).then(() =>
          clock.tween(path, ms * 0.6, {
            t: 1,
            ease: 'power2.in',
            onUpdate: () => {
              const t = path.t;
              s.x = sx + (to.x - sx) * t;
              s.y = sy + (to.y - sy) * t - Math.sin(t * Math.PI) * lift;
            },
          }).then(() => {
            onLand?.(i);
            this.give(s);
          }),
        ),
      );
    }
    await Promise.all(jobs);
  }

  /** Radial burst that falls with gravity (coin bursts, confetti coins, sparkles). */
  burst(id: string, at: Point, count: number, power = 60, ms = 700): void {
    if (clock.isSkipping) return;
    for (let i = 0; i < count; i++) {
      const s = this.take(id);
      if (!s) return;
      s.position.set(at.x, at.y);
      const a = Math.random() * Math.PI * 2;
      const v = power * (0.4 + Math.random() * 0.6);
      const st = { t: 0 };
      const x0 = at.x,
        y0 = at.y;
      clock
        .tween(st, ms, {
          t: 1,
          ease: 'none',
          onUpdate: () => {
            const t = st.t;
            s.x = x0 + Math.cos(a) * v * t;
            s.y = y0 + Math.sin(a) * v * t * 0.7 + 120 * t * t;
            s.alpha = 1 - t * t;
          },
        })
        .then(() => this.give(s));
    }
  }

  /** Coins raining from the top of the screen (LEGENDARY). */
  rain(id: string, width: number, count: number, ms = 2000): void {
    if (clock.isSkipping) return;
    for (let i = 0; i < count; i++) {
      const s = this.take(id);
      if (!s) return;
      const x = Math.random() * width;
      s.position.set(x, -10 - Math.random() * 60);
      const st = { t: 0 };
      const y0 = s.y;
      clock
        .wait(Math.random() * ms * 0.5)
        .then(() =>
          clock.tween(st, ms * 0.6, {
            t: 1,
            ease: 'power1.in',
            onUpdate: () => {
              s.y = y0 + st.t * 420;
            },
          }),
        )
        .then(() => this.give(s));
    }
  }
}
